import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Search, Pencil, MapPin, Loader2, X, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

export default function CitiesAdminPage() {
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadData = () => {
    setLoading(true);
    adminAPI.getCities(search || undefined).then(r => setCities(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, [search]);

  const startEdit = (city) => {
    setEditingId(city.id);
    setEditForm({ is_active: city.is_active, seo_text: city.seo_text || '', meta_title: city.meta_title || '', meta_description: city.meta_description || '' });
  };

  const handleSave = async (cityId) => {
    try {
      await adminAPI.updateCity(cityId, editForm);
      toast.success('Sehir guncellendi');
      setEditingId(null);
      loadData();
    } catch { toast.error('Guncellenemedi'); }
  };

  const toggleActive = async (city) => {
    try {
      await adminAPI.updateCity(city.id, { is_active: !city.is_active });
      toast.success(`${city.name} ${!city.is_active ? 'aktif' : 'pasif'} yapildi`);
      loadData();
    } catch { toast.error('Guncellenemedi'); }
  };

  return (
    <div data-testid="admin-cities">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Sehirler ({cities.length})</h1>
      </div>

      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Sehir ara..." className="pl-9" data-testid="cities-search" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cities.map(city => (
            <Card key={city.id} data-testid={`city-admin-${city.slug}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[hsl(221,83%,22%)]" />
                    <span className="font-semibold">{city.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={city.is_active} onCheckedChange={() => toggleActive(city)} />
                    <Button variant="ghost" size="sm" onClick={() => startEdit(city)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground font-mono">/{city.slug}</p>
                
                {editingId === city.id && (
                  <div className="mt-3 pt-3 border-t space-y-2">
                    <div><Label className="text-xs">SEO Title</Label><Input value={editForm.meta_title} onChange={e => setEditForm(f => ({...f, meta_title: e.target.value}))} className="h-8 text-xs" /></div>
                    <div><Label className="text-xs">SEO Description</Label><Input value={editForm.meta_description} onChange={e => setEditForm(f => ({...f, meta_description: e.target.value}))} className="h-8 text-xs" /></div>
                    <div><Label className="text-xs">SEO Metin</Label><textarea value={editForm.seo_text} onChange={e => setEditForm(f => ({...f, seo_text: e.target.value}))} rows={2} className="flex w-full rounded-md border border-input bg-background px-2 py-1 text-xs" /></div>
                    <div className="flex gap-1">
                      <Button size="sm" className="h-7 text-xs" onClick={() => handleSave(city.id)}><Check className="h-3 w-3 mr-1" /> Kaydet</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingId(null)}><X className="h-3 w-3" /></Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
